import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MoonIcon } from '../icons';

interface StoryArtworkProps {
  src?: string | null;
  srcSet?: string | null;
  sizes?: string;
  alt?: string;
  imageClassName?: string;
  fallbackClassName: string;
  fallbackSize?: number;
  testId?: string;
  loading?: 'eager' | 'lazy';
}

/**
 * Render story artwork without letting a stale/private asset leave a broken
 * image in the bedtime surfaces. The fallback is intentionally decorative:
 * story titles and actions remain the accessible source of meaning.
 */
const StoryArtwork: React.FC<StoryArtworkProps> = ({
  src,
  srcSet,
  sizes = "160px",
  alt = '',
  imageClassName,
  fallbackClassName,
  fallbackSize = 28,
  testId,
  loading,
}) => {
  const source = src || null;
  const imageRef = useRef<HTMLImageElement>(null);
  const placeholderRef = useRef<HTMLDivElement>(null);
  const [inRange, setInRange] = useState(() => loading !== 'lazy' || typeof IntersectionObserver === 'undefined');
  useEffect(() => {
    if (inRange || loading !== 'lazy' || !placeholderRef.current) return;
    // Native lazy loading can fetch images several screens away. Keep large
    // chapter illustrations close to the visible rail on constrained networks.
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setInRange(true); observer.disconnect(); }
    }, { rootMargin: '160px' });
    observer.observe(placeholderRef.current);
    return () => observer.disconnect();
  }, [inRange, loading, source]);
  const needsCacheWarm = useRef(!navigator.serviceWorker?.controller);
  const saveVisibleArtwork = useCallback(() => {
    const image = imageRef.current;
    // Wait for the browser to finish selecting/decoding the responsive source.
    // An image already in flight when the worker claims this page bypasses it.
    if (!needsCacheWarm.current || !navigator.serviceWorker?.controller || !image?.complete || !image.naturalWidth || !image.currentSrc) return;
    const url = new URL(image.currentSrc, window.location.href);
    if (url.origin === window.location.origin && /^\/api\/stories\/[^/]+\/artwork$/.test(url.pathname)) {
      needsCacheWarm.current = false;
      void fetch(url.href).catch(() => { needsCacheWarm.current = true; });
    }
  }, []);
  useEffect(() => {
    if (!navigator.serviceWorker) return;
    navigator.serviceWorker.addEventListener('controllerchange', saveVisibleArtwork);
    return () => navigator.serviceWorker.removeEventListener('controllerchange', saveVisibleArtwork);
  }, [saveVisibleArtwork]);
  const [failedSource, setFailedSource] = useState<string | null>(null);

  useEffect(() => {
    setFailedSource(null);
  }, [source]);

  if (!source || failedSource === source || (!inRange && loading === 'lazy')) {
    return (
      <div ref={placeholderRef} className={fallbackClassName} aria-hidden="true" data-testid={testId}>
        <MoonIcon size={fallbackSize} />
      </div>
    );
  }

  return (
    <img
      ref={imageRef}
      src={source}
      srcSet={srcSet || undefined}
      sizes={srcSet ? sizes : undefined}
      loading={inRange ? 'eager' : loading}
      decoding="async"
      alt={alt}
      className={imageClassName}
      data-testid={testId}
      onLoad={saveVisibleArtwork}
      onError={() => setFailedSource(source)}
    />
  );
};

export default StoryArtwork;
